import type {
  CrudDeletePayload,
  CrudEditPayload,
  CrudLoadByIdPayload,
} from '@indutrust/shared-model';
import { type } from '@ngrx/signals';
import type { EntityId } from '@ngrx/signals/entities';
import { eventGroup } from '@ngrx/signals/events';

/**
 * Factory function to create a standardized event group for single entity CRUD operations.
 *
 * Generates events for loadById, edit, and delete operations,
 * each with success and failure variants.
 *
 * @template Entity The entity type for this CRUD event group.
 * @param source The event source namespace (e.g., 'shared.country.single', 'shared.project.single').
 *               Defaults to 'shared.crudSingle' if not provided.
 * @returns An event group with CRUD lifecycle events for a single entity.
 * @example
 * ```typescript
 * // With custom source
 * export const countrySingleEvents = createCrudSingleEvents<Country>('shared.country.single');
 *
 * // With default source
 * export const projectSingleEvents = createCrudSingleEvents<Project>();
 * ```
 */
export function createCrudSingleEvents<Entity extends { id: EntityId }>(
  source = 'shared'
) {
  return eventGroup({
    source: source + '.crudSingle',
    events: {
      // LoadById events
      loadByIdRequested: type<CrudLoadByIdPayload>(),
      loadByIdSucceeded: type<Entity>(),
      loadByIdFailed: type<string>(),

      // Edit events
      editRequested: type<CrudEditPayload<Entity>>(),
      editSucceeded: type<Entity>(),
      editFailed: type<string>(),

      // Delete events
      deleteRequested: type<CrudDeletePayload>(),
      deleteSucceeded: type<EntityId>(),
      deleteFailed: type<string>(),
    },
  });
}
